import * as yup from 'yup';

import type { FormInputType } from 'components/technical/form/Form.types.ts';
import { IObjectiveType } from '../../../../../generated/graphql.tsx';
import type { AssetLabelInput } from '../../../../market/asset/AssetLabelService.ts';
import { PORTFOLIO_EXPECTED_BETA_METRIC } from '../../../../metrics/PortfolioRiskMeasures.ts';

export interface ObjectivesStepOutput<REQUIRED_AMOUNT extends boolean = true> {
  portfolioAmount: REQUIRED_AMOUNT extends true ? number : number | null;
  allowShortAndLeverage: boolean;
  objectives: {
    type: IObjectiveType;
    riskMetric: Label;
    benchmark?: (AssetLabelInput & { label: string }) | null | undefined;
  }[];
  objectiveLength?: unknown;
}

export type ObjectivesStepInput<REQUIRED_AMOUNT extends boolean = true> = FormInputType<
  Omit<ObjectivesStepOutput<REQUIRED_AMOUNT>, 'objectives'>
> & {
  objectives: {
    type: IObjectiveType | null;
    riskMetric: Label | null;
    benchmark: AssetLabelInput | null;
  }[];
};

export const createSchema = (requiredAmount: boolean) =>
  yup.object({
    ...(requiredAmount ? { portfolioAmount: yup.number().positive() } : {}),
    allowShortAndLeverage: yup.boolean().required(),
    objectives: yup
      .array(
        yup.object({
          type: yup.string().required().oneOf(Object.values(IObjectiveType)),
          riskMetric: yup.string().required(),
          benchmark: yup.mixed().when('riskMetric', {
            is: PORTFOLIO_EXPECTED_BETA_METRIC,
            // biome-ignore lint/suspicious/noThenProperty: yup valid property
            then: (schema) => schema.required(),
            otherwise: (schema) => schema.optional().nullable(),
          }),
        })
      )
      .required(),
    objectiveLength: yup
      .mixed()
      .nullable()
      .optional()
      .when('objectives', ([field], schema) =>
        schema.test('atLeastOneObjective', 'At least one objective is required', () => field.length >= 1)
      ),
  });
